import { Injectable } from '@angular/core';
import { CSS_DECLARATION_EXPR_PREFIX, CSS_VALUE_EXPR_PREFIX, TemplateStructurePreprocessorService } from './template-structure-preprocessor.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { escapeHtml } from 'src/app/shared/helpers/html-escape.helper';
import { TemplateRenderResult } from 'src/app/models/reportTemplate.model';
import { isLenientSource } from 'src/app/shared/helpers/lenient-template-source';
import * as Sentry from '@sentry/angular';

type TemplateContextValue = unknown;

enum PathLookupState {
    Found = 'found',
    NullParent = 'null-parent',
    Absent = 'absent'
}

interface PathLookup {
    readonly state: PathLookupState;
    readonly value: TemplateContextValue;
}

interface Collected<T> {
    readonly value: T;
    readonly paths: string[];
}

interface RenderScope {
    readonly unresolved: string[];
    readonly data: TemplateContextValue;
    readonly index: number;
    readonly isFirst: boolean;
    readonly isLast: boolean;
    readonly parent: RenderScope | null;
    readonly root: TemplateContextValue;
}

type BinaryOperator = '>' | '>=' | '<' | '<=' | '==' | '!=' | '+' | '-' | '*' | '/' | '&&' | '||';

interface PathExprNode {
    readonly kind: 'path';
    readonly path: string;
}

interface NumberExprNode {
    readonly kind: 'number';
    readonly value: number;
}

interface StringExprNode {
    readonly kind: 'string';
    readonly value: string;
}

interface BooleanExprNode {
    readonly kind: 'boolean';
    readonly value: boolean;
}

interface NotExprNode {
    readonly kind: 'not';
    readonly operand: ExprNode;
}

interface BinaryExprNode {
    readonly kind: 'binary';
    readonly operator: BinaryOperator;
    readonly left: ExprNode;
    readonly right: ExprNode;
}

interface PipeExprNode {
    readonly kind: 'pipe';
    readonly target: ExprNode;
    readonly pipeName: string;
    readonly args: ExprNode[];
}

type ExprNode = PathExprNode | NumberExprNode | StringExprNode | BooleanExprNode | NotExprNode | BinaryExprNode | PipeExprNode;

enum ExprTokenKind {
    Path = 'path',
    Number = 'number',
    String = 'string',
    Operator = 'operator',
    Pipe = 'pipe',
    Colon = 'colon',
    Comma = 'comma',
    LParen = 'lparen',
    RParen = 'rparen',
    Unknown = 'unknown'
}

interface ExprToken {
    readonly kind: ExprTokenKind;
    readonly value: string;
}

type ScalarPipeFn = (value: TemplateContextValue, args: ExprNode[], scope: RenderScope) => TemplateContextValue;
type ArrayPipeFn = (collection: TemplateContextValue, args: ExprNode[]) => TemplateContextValue;

interface ExpressionParser {
    readonly parseExpression: () => ExprNode;
    readonly parsePiped: () => ExprNode;
    readonly isFullyParsed: () => boolean;
}

enum TemplateNodeKind {
    Text = 'text',
    Scalar = 'scalar',
    Each = 'each',
    If = 'if'
}

interface TextNode {
    readonly kind: TemplateNodeKind.Text;
    readonly value: string;
}

enum ValueEscapeContext {
    Html = 'html',
    CssValue = 'css-value',
    CssDeclaration = 'css-declaration'
}

interface ScalarNode {
    readonly kind: TemplateNodeKind.Scalar;
    readonly path: string;
    readonly escapeContext: ValueEscapeContext;
    readonly markerVisible: boolean;
}

interface EachNode {
    readonly kind: TemplateNodeKind.Each;
    readonly collection: string;
    readonly body: TemplateNode[];
    readonly markerVisible: boolean;
}

interface IfNode {
    readonly kind: TemplateNodeKind.If;
    readonly condition: string;
    readonly body: TemplateNode[];
    readonly markerVisible: boolean;
}

type TemplateNode = TextNode | ScalarNode | EachNode | IfNode;

enum TemplateTokenKind {
    Text = 'text',
    OpenEach = 'open-each',
    OpenIf = 'open-if',
    Close = 'close'
}

type TemplateToken =
    | { type: TemplateTokenKind.Text; value: string; start: number }
    | { type: TemplateTokenKind.OpenEach; expression: string; markerVisible: boolean }
    | { type: TemplateTokenKind.OpenIf; expression: string; markerVisible: boolean }
    | { type: TemplateTokenKind.Close; block: 'each' | 'if' };

const BLOCK_TOKEN_REGEX = /\[\[\s*(#each\s+[^\]]+|\/each|#if\s+[^\]]+|\/if)\s*\]\]/g;
const SCALAR_TOKEN_REGEX = /\[\[([^\]]+)\]\]/g;
const INDEX_KEYWORD = '@index';
const FIRST_KEYWORD = '@first';
const LAST_KEYWORD = '@last';
const THIS_KEYWORD = 'this';
const PARENT_PREFIX = '../';
const COMPARISON_OPERATORS: ReadonlySet<string> = new Set(['>=', '<=', '==', '!=', '>', '<']);
const AND_OPERATOR = '&&';
const OR_OPERATOR = '||';
const DESCENDING_ARG = 'desc';
const ASCENDING_ARG = 'asc';
const SORT_DIRECTION_ARGS: ReadonlySet<string> = new Set([DESCENDING_ARG, ASCENDING_ARG]);
const BOOLEAN_LITERALS: ReadonlyMap<string, boolean> = new Map([['true', true], ['false', false]]);
const NEGATION_OPERATOR = '-';
const NOT_OPERATOR = '!';
const MAX_EXPRESSION_TOKENS = 256;
const MAX_DECIMAL_PLACES = 20;
const UNSAFE_KEY_SEGMENTS: ReadonlySet<string> = new Set(['__proto__', 'constructor', 'prototype']);
const EXPR_TWO_CHAR_OPERATOR_REGEX = /^(>=|<=|==|!=|&&|\|\|)/;
const EXPR_STRING_LITERAL_REGEX = /^'([^']*)'/;
const EXPR_NUMBER_LITERAL_REGEX = /^\d+(?:\.\d+)?/;
const EXPR_PATH_LITERAL_REGEX = /^(?:\.\.\/)*[@A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+)*/;
const EXPR_WHITESPACE_REGEX = /^\s+/;
const CSS_STRUCTURAL_REGEX = /["'\\<>{}]|\/\*|\*\//g;
const CSS_DECLARATION_SEPARATOR_REGEX = /;/g;
const CSS_RESOURCE_FUNCTION_REGEX = /(?:url|image|image-set|cross-fade|element|expression)\s*\(/gi;
const STYLE_OPEN_TAG = '<style';
const STYLE_CLOSE_TAG = '</style';

@Injectable({ providedIn: 'root' })
export class TemplateProcessorService {
    private readonly scalarPipes: ReadonlyMap<string, ScalarPipeFn>;
    private readonly arrayPipes: ReadonlyMap<string, ArrayPipeFn>;
    private readonly reportedSignatures = new Set<string>();

    constructor(
        private structurePreprocessor: TemplateStructurePreprocessorService,
        private dateFormatService: DateFormatService,
        private valueConversionService: ValueConversionService
    ) {
        this.scalarPipes = this.buildScalarPipes();
        this.arrayPipes = this.buildArrayPipes();
    }

    processPlaceholders(html: string, context: TemplateContextValue): string {
        const result = this.render(html, context);
        if (result.unresolvedPaths.length) this.reportUnresolvedOnce(html, result.unresolvedPaths);
        return result.html;
    }

    private reportUnresolvedOnce(html: string, paths: string[]): void {
        const signature = `${this.hashText(html)}:${paths.join('|')}`;
        if (this.reportedSignatures.has(signature)) return;

        this.reportedSignatures.add(signature);
        Sentry.captureMessage('Report template rendered with unresolvable placeholders', {
            level: 'warning',
            extra: { paths }
        });
    }

    private hashText(text: string): number {
        let hash = 5381;
        for (let index = 0; index < text.length; index++) {
            hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
        }
        return hash;
    }

    private isMarkerVisible(html: string, index: number): boolean {
        if (index <= 0) return true;
        if (html.lastIndexOf('<', index - 1) > html.lastIndexOf('>', index - 1)) return false;
        const lowered = html.toLowerCase();
        return lowered.lastIndexOf(STYLE_OPEN_TAG, index - 1) <= lowered.lastIndexOf(STYLE_CLOSE_TAG, index - 1);
    }

    render(html: string, context: TemplateContextValue): TemplateRenderResult {
        const expanded = this.structurePreprocessor.expandStructuralAttributes(html);
        const processed = this.processNgIfDirectives(expanded, context);
        if (!processed.includes('[[')) return { html: processed, unresolvedPaths: [] };

        const unresolved: string[] = [];
        const rootScope: RenderScope = { data: context, index: 0, isFirst: true, isLast: true, parent: null, root: context, unresolved };

        const rendered = this.hasBlockTokens(processed)
            ? this.renderNodes(this.parseTemplate(processed), rootScope)
            : this.substituteScalars(processed, rootScope);
        return { html: rendered, unresolvedPaths: [...new Set(unresolved)] };
    }

    private collect<T>(scope: RenderScope, evaluate: () => T): Collected<T> {
        const start = scope.unresolved.length;
        const value = evaluate();
        return { value, paths: scope.unresolved.slice(start) };
    }

    private buildMarker(paths: string[]): string {
        return escapeHtml(`{{MISSING: ${paths.join(', ')}}}`);
    }

    private hasBlockTokens(html: string): boolean {
        BLOCK_TOKEN_REGEX.lastIndex = 0;
        const found = BLOCK_TOKEN_REGEX.test(html);
        BLOCK_TOKEN_REGEX.lastIndex = 0;
        return found;
    }

    private parseTemplate(html: string): TemplateNode[] {
        const tokens = this.tokenize(html);
        let cursor = 0;

        const parseBlock = (closer: 'each' | 'if' | null): TemplateNode[] => {
            const nodes: TemplateNode[] = [];
            while (cursor < tokens.length) {
                const token = tokens[cursor];

                if (token.type === TemplateTokenKind.Close) {
                    if (closer && token.block === closer) {
                        cursor++;
                        return nodes;
                    }
                    cursor++;
                    continue;
                }

                if (token.type === TemplateTokenKind.OpenEach) {
                    cursor++;
                    const body = parseBlock('each');
                    nodes.push({ kind: TemplateNodeKind.Each, collection: token.expression, body, markerVisible: token.markerVisible });
                    continue;
                }

                if (token.type === TemplateTokenKind.OpenIf) {
                    cursor++;
                    const body = parseBlock('if');
                    nodes.push({ kind: TemplateNodeKind.If, condition: token.expression, body, markerVisible: token.markerVisible });
                    continue;
                }

                cursor++;
                nodes.push(...this.parseTextSegment(token.value, token.start, html));
            }
            return nodes;
        };

        return parseBlock(null);
    }

    private tokenize(html: string): TemplateToken[] {
        const tokens: TemplateToken[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        BLOCK_TOKEN_REGEX.lastIndex = 0;

        while ((match = BLOCK_TOKEN_REGEX.exec(html)) !== null) {
            if (match.index > lastIndex) {
                tokens.push({ type: TemplateTokenKind.Text, value: html.slice(lastIndex, match.index), start: lastIndex });
            }
            const directive = match[1].trim();
            const markerVisible = this.isMarkerVisible(html, match.index);
            if (directive.startsWith('#each')) {
                tokens.push({ type: TemplateTokenKind.OpenEach, expression: directive.slice('#each'.length).trim(), markerVisible });
            } else if (directive === '/each') {
                tokens.push({ type: TemplateTokenKind.Close, block: 'each' });
            } else if (directive.startsWith('#if')) {
                tokens.push({ type: TemplateTokenKind.OpenIf, expression: directive.slice('#if'.length).trim(), markerVisible });
            } else {
                tokens.push({ type: TemplateTokenKind.Close, block: 'if' });
            }
            lastIndex = match.index + match[0].length;
        }
        BLOCK_TOKEN_REGEX.lastIndex = 0;

        if (lastIndex < html.length) {
            tokens.push({ type: TemplateTokenKind.Text, value: html.slice(lastIndex), start: lastIndex });
        }
        return tokens;
    }

    private parseTextSegment(segment: string, segmentStart: number, html: string): TemplateNode[] {
        const nodes: TemplateNode[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        SCALAR_TOKEN_REGEX.lastIndex = 0;

        while ((match = SCALAR_TOKEN_REGEX.exec(segment)) !== null) {
            if (match.index > lastIndex) {
                nodes.push({ kind: TemplateNodeKind.Text, value: segment.slice(lastIndex, match.index) });
            }
            nodes.push({
                kind: TemplateNodeKind.Scalar,
                ...this.readScalarToken(match[1]),
                markerVisible: this.isMarkerVisible(html, segmentStart + match.index)
            });
            lastIndex = match.index + match[0].length;
        }
        SCALAR_TOKEN_REGEX.lastIndex = 0;

        if (lastIndex < segment.length) {
            nodes.push({ kind: TemplateNodeKind.Text, value: segment.slice(lastIndex) });
        }
        return nodes;
    }

    private renderNodes(nodes: TemplateNode[], scope: RenderScope): string {
        let output = '';
        for (const node of nodes) {
            output += this.renderNode(node, scope);
        }
        return output;
    }

    private renderNode(node: TemplateNode, scope: RenderScope): string {
        switch (node.kind) {
            case TemplateNodeKind.Text:
                return node.value;
            case TemplateNodeKind.Scalar:
                return this.renderScalar(node.path, scope, node.escapeContext, node.markerVisible);
            case TemplateNodeKind.If: {
                const { value: matches, paths } = this.collect(scope, () => this.evaluateCondition(node.condition, scope));
                if (paths.length) return node.markerVisible ? this.buildMarker(paths) : '';
                return matches ? this.renderNodes(node.body, scope) : '';
            }
            case TemplateNodeKind.Each:
                return this.renderEach(node, scope);
        }
    }

    private renderEach(node: EachNode, scope: RenderScope): string {
        const { value: collection, paths } = this.collect<TemplateContextValue>(scope, () => {
            const parser = this.createExpressionParser(node.collection);
            const collectionExpr = parser.parsePiped();
            if (!parser.isFullyParsed()) {
                scope.unresolved.push(node.collection);
                return null;
            }
            return this.evaluateCollectionExpr(collectionExpr, scope);
        });
        if (paths.length) return node.markerVisible ? this.buildMarker(paths) : '';
        if (!Array.isArray(collection)) return '';

        let output = '';
        for (let index = 0; index < collection.length; index++) {
            const itemScope: RenderScope = {
                data: collection[index],
                index,
                isFirst: index === 0,
                isLast: index === collection.length - 1,
                parent: scope,
                root: scope.root,
                unresolved: scope.unresolved
            };
            output += this.renderNodes(node.body, itemScope);
        }
        return output;
    }

    private readScalarToken(rawToken: string): { path: string; escapeContext: ValueEscapeContext } {
        const token = rawToken.trim();
        if (token.startsWith(CSS_VALUE_EXPR_PREFIX)) {
            return { path: token.slice(CSS_VALUE_EXPR_PREFIX.length).trim(), escapeContext: ValueEscapeContext.CssValue };
        }
        if (token.startsWith(CSS_DECLARATION_EXPR_PREFIX)) {
            return { path: token.slice(CSS_DECLARATION_EXPR_PREFIX.length).trim(), escapeContext: ValueEscapeContext.CssDeclaration };
        }
        return { path: token, escapeContext: ValueEscapeContext.Html };
    }

    private renderScalar(path: string, scope: RenderScope, escapeContext: ValueEscapeContext, markerVisible: boolean): string {
        const { value, paths } = this.collect<TemplateContextValue>(scope, () => {
            const parser = this.createExpressionParser(path);
            const expression = parser.parseExpression();
            if (!parser.isFullyParsed()) {
                scope.unresolved.push(path);
                return null;
            }
            return this.evaluateExpression(expression, scope);
        });
        if (paths.length) return escapeContext === ValueEscapeContext.Html && markerVisible ? this.buildMarker(paths) : '';

        return this.formatValue(value, escapeContext);
    }

    private formatValue(value: TemplateContextValue, escapeContext: ValueEscapeContext): string {
        if (value === undefined || value === null) return '';
        if (typeof value === 'object') {
            if (value instanceof Date) return this.escapeValue(value.toLocaleDateString(), escapeContext);
            if (Array.isArray(value)) {
                const allPrimitive = value.every(v => v === null || v === undefined || typeof v !== 'object');
                return allPrimitive ? this.escapeValue(value.join(', '), escapeContext) : '';
            }
            return '';
        }
        return this.escapeValue(String(value), escapeContext);
    }

    private resolvePath(rawPath: string, scope: RenderScope): TemplateContextValue {
        let path = rawPath.trim();
        let currentScope = scope;

        while (path.startsWith(PARENT_PREFIX)) {
            currentScope = currentScope.parent ?? currentScope;
            path = path.slice(PARENT_PREFIX.length).trim();
        }

        if (path === INDEX_KEYWORD) return currentScope.index;
        if (path === FIRST_KEYWORD) return currentScope.isFirst;
        if (path === LAST_KEYWORD) return currentScope.isLast;
        if (path === THIS_KEYWORD || path === '') return currentScope.data;

        if (path.startsWith(`${THIS_KEYWORD}.`)) {
            const own = this.lookupPath(currentScope.data, path.slice(THIS_KEYWORD.length + 1));
            if (own.state === PathLookupState.Absent) scope.unresolved.push(rawPath.trim());
            return own.value;
        }

        const fromItem = this.lookupPath(currentScope.data, path);
        if (fromItem.state === PathLookupState.Found && fromItem.value !== undefined) return fromItem.value;

        const fromRoot = this.lookupPath(currentScope.root, path);
        if (fromRoot.state === PathLookupState.Absent && fromItem.state === PathLookupState.Absent) {
            scope.unresolved.push(rawPath.trim());
        }
        return fromRoot.value;
    }

    private lookupPath(obj: TemplateContextValue, path: string): PathLookup {
        let current = obj;
        for (const prop of path.split('.')) {
            if (current === null || current === undefined || current === '') return { state: PathLookupState.NullParent, value: undefined };
            if (UNSAFE_KEY_SEGMENTS.has(prop) || typeof current !== 'object') return { state: PathLookupState.Absent, value: undefined };
            const holder = current as Record<string, unknown>;
            const inheritedFromObject = prop in Object.prototype && !Object.prototype.hasOwnProperty.call(holder, prop);
            if (!(prop in holder) || inheritedFromObject) {
                return { state: isLenientSource(current) ? PathLookupState.NullParent : PathLookupState.Absent, value: undefined };
            }
            current = (current as Record<string, unknown>)[prop];
        }
        return { state: PathLookupState.Found, value: current };
    }

    private evaluateCondition(rawCondition: string, scope: RenderScope): boolean {
        const parser = this.createExpressionParser(rawCondition.trim());
        const expression = parser.parseExpression();
        if (!parser.isFullyParsed()) {
            scope.unresolved.push(rawCondition.trim());
            return false;
        }

        return !!this.evaluateExpression(expression, scope);
    }

    private substituteScalars(html: string, scope: RenderScope): string {
        SCALAR_TOKEN_REGEX.lastIndex = 0;
        return html.replace(SCALAR_TOKEN_REGEX, (_, fieldPath: string, offset: number) => {
            const { path, escapeContext } = this.readScalarToken(fieldPath);
            return this.renderScalar(path, scope, escapeContext, this.isMarkerVisible(html, offset));
        });
    }

    private escapeValue(text: string, escapeContext: ValueEscapeContext): string {
        if (escapeContext === ValueEscapeContext.Html) return escapeHtml(text);
        return this.escapeCss(text, escapeContext === ValueEscapeContext.CssValue);
    }

    private escapeCss(text: string, isValuePosition: boolean): string {
        let stripped = this.stripUntilStable(text, CSS_STRUCTURAL_REGEX);
        if (isValuePosition) stripped = stripped.replace(CSS_DECLARATION_SEPARATOR_REGEX, '');
        return escapeHtml(this.stripUntilStable(stripped, CSS_RESOURCE_FUNCTION_REGEX));
    }

    private stripUntilStable(text: string, pattern: RegExp): string {
        let current = text;
        let previous: string;
        do {
            previous = current;
            current = current.replace(pattern, '');
        } while (current !== previous);
        return current;
    }

    private processNgIfDirectives(html: string, context: TemplateContextValue): string {
        let processed = html;
        let hasChanges = true;

        while (hasChanges) {
            const beforeLength = processed.length;
            const ngIfMatch = this.findNgIfElement(processed);

            if (ngIfMatch) {
                const { fullMatch, condition, startIndex, endIndex } = ngIfMatch;
                const shouldShow = this.evaluateLegacyCondition(condition, context);

                if (shouldShow) {
                    const cleaned = fullMatch.replace(/\*ngIf="[^"]+"\s*/g, '');
                    processed = processed.substring(0, startIndex) + cleaned + processed.substring(endIndex);
                } else {
                    processed = processed.substring(0, startIndex) + processed.substring(endIndex);
                }
            }

            hasChanges = processed.length !== beforeLength;
        }

        return processed;
    }

    private findNgIfElement(html: string): {
        fullMatch: string;
        condition: string;
        startIndex: number;
        endIndex: number;
    } | null {
        const openTagRegex = /<([a-zA-Z][a-zA-Z0-9-]*)\s+[^>]*\*ngIf="([^"]+)"[^>]*>/;
        const openMatch = html.match(openTagRegex);

        if (!openMatch) return null;

        const tagName = openMatch[1];
        const condition = openMatch[2];
        const startIndex = openMatch.index!;
        const openTagEnd = startIndex + openMatch[0].length;

        let depth = 1;
        const tagRegex = new RegExp(`<(/?)${tagName}(?:\\s[^>]*)?>`, 'g');
        tagRegex.lastIndex = openTagEnd;

        let match: RegExpExecArray | null;
        while ((match = tagRegex.exec(html)) !== null) {
            if (match[1] === '/') {
                depth--;
                if (depth === 0) {
                    const endIndex = match.index + match[0].length;
                    return {
                        fullMatch: html.substring(startIndex, endIndex),
                        condition,
                        startIndex,
                        endIndex
                    };
                }
            } else {
                depth++;
            }
        }

        return null;
    }

    private evaluateLegacyCondition(condition: string, context: TemplateContextValue): boolean {
        try {
            if (condition.startsWith('!')) {
                return !this.getNestedProperty(context, condition.substring(1).trim());
            }
            return !!this.getNestedProperty(context, condition);
        } catch {
            return false;
        }
    }

    private getNestedProperty(obj: TemplateContextValue, path: string): TemplateContextValue {
        return this.lookupPath(obj, path).value;
    }

    private tokenizeExpression(source: string): ExprToken[] {
        const tokens: ExprToken[] = [];
        let pos = 0;

        while (pos < source.length) {
            const remaining = source.slice(pos);

            const whitespace = EXPR_WHITESPACE_REGEX.exec(remaining);
            if (whitespace) {
                pos += whitespace[0].length;
                continue;
            }

            const twoCharOperator = EXPR_TWO_CHAR_OPERATOR_REGEX.exec(remaining);
            if (twoCharOperator) {
                tokens.push({ kind: ExprTokenKind.Operator, value: twoCharOperator[0] });
                pos += twoCharOperator[0].length;
                continue;
            }

            const singleChar = remaining[0];
            if ('><+-*/!'.includes(singleChar)) {
                tokens.push({ kind: ExprTokenKind.Operator, value: singleChar });
                pos += 1;
                continue;
            }
            if (singleChar === '|') {
                tokens.push({ kind: ExprTokenKind.Pipe, value: singleChar });
                pos += 1;
                continue;
            }
            if (singleChar === ':') {
                tokens.push({ kind: ExprTokenKind.Colon, value: singleChar });
                pos += 1;
                continue;
            }
            if (singleChar === ',') {
                tokens.push({ kind: ExprTokenKind.Comma, value: singleChar });
                pos += 1;
                continue;
            }
            if (singleChar === '(') {
                tokens.push({ kind: ExprTokenKind.LParen, value: singleChar });
                pos += 1;
                continue;
            }
            if (singleChar === ')') {
                tokens.push({ kind: ExprTokenKind.RParen, value: singleChar });
                pos += 1;
                continue;
            }

            const stringLiteral = EXPR_STRING_LITERAL_REGEX.exec(remaining);
            if (stringLiteral) {
                tokens.push({ kind: ExprTokenKind.String, value: stringLiteral[1] });
                pos += stringLiteral[0].length;
                continue;
            }

            const numberLiteral = EXPR_NUMBER_LITERAL_REGEX.exec(remaining);
            if (numberLiteral) {
                tokens.push({ kind: ExprTokenKind.Number, value: numberLiteral[0] });
                pos += numberLiteral[0].length;
                continue;
            }

            const pathLiteral = EXPR_PATH_LITERAL_REGEX.exec(remaining);
            if (pathLiteral) {
                tokens.push({ kind: ExprTokenKind.Path, value: pathLiteral[0] });
                pos += pathLiteral[0].length;
                continue;
            }

            tokens.push({ kind: ExprTokenKind.Unknown, value: singleChar });
            pos += 1;
        }

        return tokens.length > MAX_EXPRESSION_TOKENS
            ? [{ kind: ExprTokenKind.Unknown, value: '' }]
            : tokens;
    }

    private createExpressionParser(source: string): ExpressionParser {
        const tokens = this.tokenizeExpression(source);
        let cursor = 0;
        let malformed = tokens.some(token => token.kind === ExprTokenKind.Unknown);

        const parsePrimary = (): ExprNode => {
            const token = tokens[cursor];
            if (!token) {
                malformed = true;
                return { kind: 'string', value: '' };
            }

            if (token.kind === ExprTokenKind.LParen) {
                cursor++;
                const inner = parseExpression();
                if (tokens[cursor]?.kind === ExprTokenKind.RParen) cursor++;
                else malformed = true;
                return inner;
            }
            if (token.kind === ExprTokenKind.Operator && token.value === NEGATION_OPERATOR) {
                cursor++;
                return { kind: 'binary', operator: NEGATION_OPERATOR, left: { kind: 'number', value: 0 }, right: parsePrimary() };
            }
            if (token.kind === ExprTokenKind.Number) {
                cursor++;
                return { kind: 'number', value: Number(token.value) };
            }
            if (token.kind === ExprTokenKind.String) {
                cursor++;
                return { kind: 'string', value: token.value };
            }
            if (token.kind === ExprTokenKind.Path) {
                cursor++;
                const booleanLiteral = BOOLEAN_LITERALS.get(token.value.toLowerCase());
                return booleanLiteral === undefined
                    ? { kind: 'path', path: token.value }
                    : { kind: 'boolean', value: booleanLiteral };
            }

            cursor++;
            malformed = true;
            return { kind: 'string', value: '' };
        };

        const isOperator = (...values: string[]): boolean =>
            tokens[cursor]?.kind === ExprTokenKind.Operator && values.includes(tokens[cursor].value);

        const parseTerm = (): ExprNode => {
            let node = parsePrimary();
            while (isOperator('*', '/')) {
                const operator = tokens[cursor].value as BinaryOperator;
                cursor++;
                node = { kind: 'binary', operator, left: node, right: parsePrimary() };
            }
            return node;
        };

        const parseSum = (): ExprNode => {
            let node = parseTerm();
            while (isOperator('+', '-')) {
                const operator = tokens[cursor].value as BinaryOperator;
                cursor++;
                node = { kind: 'binary', operator, left: node, right: parseTerm() };
            }
            return node;
        };

        const parsePiped = (): ExprNode => {
            let node = parseSum();
            while (tokens[cursor]?.kind === ExprTokenKind.Pipe) {
                cursor++;
                const nameToken = tokens[cursor];
                if (!nameToken || nameToken.kind !== ExprTokenKind.Path) {
                    malformed = true;
                    break;
                }
                cursor++;

                const args: ExprNode[] = [];
                if (tokens[cursor]?.kind === ExprTokenKind.Colon) {
                    cursor++;
                    args.push(parsePrimary());
                    while (tokens[cursor]?.kind === ExprTokenKind.Comma) {
                        cursor++;
                        args.push(parsePrimary());
                    }
                }
                node = { kind: 'pipe', target: node, pipeName: nameToken.value, args };
            }
            return node;
        };

        const parseComparison = (): ExprNode => {
            const left = parsePiped();
            const operatorToken = tokens[cursor];
            if (operatorToken?.kind === ExprTokenKind.Operator && COMPARISON_OPERATORS.has(operatorToken.value)) {
                cursor++;
                return { kind: 'binary', operator: operatorToken.value as BinaryOperator, left, right: parsePiped() };
            }
            return left;
        };

        const parseNegation = (): ExprNode => {
            if (!isOperator(NOT_OPERATOR)) return parseComparison();
            cursor++;
            return { kind: 'not', operand: parseNegation() };
        };

        const parseConjunction = (): ExprNode => {
            let node = parseNegation();
            while (isOperator(AND_OPERATOR)) {
                cursor++;
                node = { kind: 'binary', operator: AND_OPERATOR, left: node, right: parseNegation() };
            }
            return node;
        };

        const parseExpression = (): ExprNode => {
            let node = parseConjunction();
            while (isOperator(OR_OPERATOR)) {
                cursor++;
                node = { kind: 'binary', operator: OR_OPERATOR, left: node, right: parseConjunction() };
            }
            return node;
        };

        const isFullyParsed = (): boolean => !malformed && cursor === tokens.length;

        return { parseExpression, parsePiped, isFullyParsed };
    }

    private evaluateExpression(node: ExprNode, scope: RenderScope): TemplateContextValue {
        switch (node.kind) {
            case 'path':
                return this.resolvePath(node.path, scope);
            case 'number':
                return node.value;
            case 'string':
                return node.value;
            case 'boolean':
                return node.value;
            case 'not':
                return !this.evaluateExpression(node.operand, scope);
            case 'binary':
                return this.evaluateBinary(node, scope);
            case 'pipe':
                return this.evaluateScalarPipe(node, scope);
        }
    }

    private evaluateBinary(node: BinaryExprNode, scope: RenderScope): TemplateContextValue {
        if (node.operator === AND_OPERATOR) {
            return !!this.evaluateExpression(node.left, scope) && !!this.evaluateExpression(node.right, scope);
        }
        if (node.operator === OR_OPERATOR) {
            return !!this.evaluateExpression(node.left, scope) || !!this.evaluateExpression(node.right, scope);
        }

        const left = this.evaluateExpression(node.left, scope);
        const right = this.evaluateExpression(node.right, scope);

        switch (node.operator) {
            case '+':
                return this.evaluateArithmetic(left, right, (a, b) => a + b);
            case '-':
                return this.evaluateArithmetic(left, right, (a, b) => a - b);
            case '*':
                return this.evaluateArithmetic(left, right, (a, b) => a * b);
            case '/':
                return this.evaluateArithmetic(left, right, (a, b) => a / b);
            case '>':
                return this.compareOrdered(left, right, order => order > 0);
            case '>=':
                return this.compareOrdered(left, right, order => order >= 0);
            case '<':
                return this.compareOrdered(left, right, order => order < 0);
            case '<=':
                return this.compareOrdered(left, right, order => order <= 0);
            case '==':
                return this.valuesEqual(left, right);
            case '!=':
                return !this.valuesEqual(left, right);
        }
    }

    private evaluateArithmetic(
        left: TemplateContextValue,
        right: TemplateContextValue,
        operation: (leftNumber: number, rightNumber: number) => number
    ): TemplateContextValue {
        const leftNumber = this.toFiniteNumber(left);
        const rightNumber = this.toFiniteNumber(right);
        if (leftNumber === null || rightNumber === null) return undefined;

        const result = operation(leftNumber, rightNumber);
        return Number.isFinite(result) ? result : undefined;
    }

    private compareOrdered(
        left: TemplateContextValue,
        right: TemplateContextValue,
        matches: (order: number) => boolean
    ): boolean {
        const order = this.compareValues(left, right);
        return order === null ? false : matches(order);
    }

    private valuesEqual(left: TemplateContextValue, right: TemplateContextValue): boolean {
        if (left === right) return true;
        if (typeof left === 'boolean' || typeof right === 'boolean') {
            const leftBoolean = this.toComparableBoolean(left);
            const rightBoolean = this.toComparableBoolean(right);
            return leftBoolean !== null && rightBoolean !== null && leftBoolean === rightBoolean;
        }
        const leftNumber = this.toFiniteNumber(left);
        const rightNumber = this.toFiniteNumber(right);
        return leftNumber !== null && rightNumber !== null && leftNumber === rightNumber;
    }

    private toComparableBoolean(value: TemplateContextValue): boolean | null {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') {
            if (value === 1) return true;
            if (value === 0) return false;
            return null;
        }
        if (typeof value === 'string') {
            const text = value.trim().toLowerCase();
            const literal = BOOLEAN_LITERALS.get(text);
            if (literal !== undefined) return literal;
        }
        return null;
    }

    private evaluateScalarPipe(node: PipeExprNode, scope: RenderScope): TemplateContextValue {
        const target = this.evaluateExpression(node.target, scope);
        const pipeFn = this.scalarPipes.get(node.pipeName);
        return pipeFn ? pipeFn(target, node.args, scope) : target;
    }

    private evaluateCollectionExpr(node: ExprNode, scope: RenderScope): TemplateContextValue {
        if (node.kind === 'pipe') {
            const target = this.evaluateCollectionExpr(node.target, scope);
            const pipeFn = this.arrayPipes.get(node.pipeName);
            return pipeFn ? pipeFn(target, node.args) : target;
        }
        if (node.kind === 'path') return this.resolvePath(node.path, scope);
        return this.evaluateExpression(node, scope);
    }

    private toNumber(value: TemplateContextValue): number {
        return typeof value === 'number' ? value : Number(value);
    }

    private toFiniteNumber(value: TemplateContextValue): number | null {
        if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
        const numeric = this.toNumber(value);
        return Number.isFinite(numeric) ? numeric : null;
    }

    private compareValues(left: TemplateContextValue, right: TemplateContextValue): number | null {
        const leftNumber = this.toFiniteNumber(left);
        const rightNumber = this.toFiniteNumber(right);
        if (leftNumber !== null && rightNumber !== null) return leftNumber - rightNumber;
        if (typeof left === 'string' && typeof right === 'string') return left.localeCompare(right);
        return null;
    }

    private compareSortValues(left: TemplateContextValue, right: TemplateContextValue): number {
        const leftMissing = left === null || left === undefined || left === '';
        const rightMissing = right === null || right === undefined || right === '';
        if (leftMissing || rightMissing) return leftMissing === rightMissing ? 0 : leftMissing ? 1 : -1;
        return this.compareValues(left, right) ?? String(left).localeCompare(String(right));
    }

    private buildScalarPipes(): ReadonlyMap<string, ScalarPipeFn> {
        return new Map<string, ScalarPipeFn>([
            ['date', value => this.formatDatePipeValue(value, this.dateFormatService.userDateFormat)],
            ['datetime', value => this.formatDatePipeValue(value, this.dateFormatService.userDateWithTimeFormat)],
            ['number', (value, args) => this.formatNumberPipe(value, args)],
            ['round', value => this.mapFiniteNumber(value, numeric => Math.round(numeric))],
            ['percent', value => this.mapFiniteNumber(value, numeric => `${Math.round(numeric)}%`)],
            ['unit', (value, args, scope) => this.formatUnitPipe(value, args, scope)],
            ['default', (value, args) => this.formatDefaultPipe(value, args)]
        ]);
    }

    private buildArrayPipes(): ReadonlyMap<string, ArrayPipeFn> {
        return new Map<string, ArrayPipeFn>([
            ['groupBy', (collection, args) => this.applyGroupBy(collection, args)],
            ['sortBy', (collection, args) => this.applySortBy(collection, args)]
        ]);
    }

    private formatDatePipeValue(value: TemplateContextValue, formatStr: string): TemplateContextValue {
        if (value === null || value === undefined || value === '') return value;
        if (typeof value === 'string') return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(value, formatStr);
        if (typeof value === 'number') return this.dateFormatService.formatUtcTimestampToUserTimezone(value, formatStr);
        if (value instanceof Date) return this.dateFormatService.formatDateTimeWithTimeZoneToString(value, formatStr);
        return value;
    }

    private mapFiniteNumber(value: TemplateContextValue, format: (numeric: number) => TemplateContextValue): TemplateContextValue {
        const numeric = this.toFiniteNumber(value);
        return numeric === null ? undefined : format(numeric);
    }

    private formatNumberPipe(value: TemplateContextValue, args: ExprNode[]): TemplateContextValue {
        const decimalPlacesArg = args[0];
        const requestedPlaces = decimalPlacesArg?.kind === 'number' ? decimalPlacesArg.value : 0;
        const decimalPlaces = Math.min(Math.max(Math.trunc(requestedPlaces), 0), MAX_DECIMAL_PLACES);
        return this.mapFiniteNumber(value, numeric => numeric.toFixed(decimalPlaces));
    }

    private formatUnitPipe(value: TemplateContextValue, args: ExprNode[], scope: RenderScope): TemplateContextValue {
        const unitsArg = args[0];
        if (!unitsArg || unitsArg.kind !== 'path') return value;

        const units = this.toUnits(this.resolvePath(unitsArg.path, scope));
        return this.mapFiniteNumber(value, numeric => {
            if (units === null) return numeric.toFixed(4);
            const convertedValue = this.valueConversionService.getParameterValueAsPerAgentUnit(numeric, units).toFixed(4);
            return `${convertedValue} ${this.valueConversionService.getParameterUnitSymbol(units)}`;
        });
    }

    private toUnits(value: TemplateContextValue): Units | null {
        const numeric = this.toFiniteNumber(value);
        return numeric !== null && UnitSymbols[numeric as Units] !== undefined ? (numeric as Units) : null;
    }

    private formatDefaultPipe(value: TemplateContextValue, args: ExprNode[]): TemplateContextValue {
        const fallbackArg = args[0];
        const fallback = fallbackArg?.kind === 'string' ? fallbackArg.value : '';
        return value === null || value === undefined || value === '' ? fallback : value;
    }

    private applyGroupBy(collection: TemplateContextValue, args: ExprNode[]): TemplateContextValue {
        if (!Array.isArray(collection)) return collection;

        const fields = args
            .filter((arg): arg is PathExprNode => arg.kind === 'path')
            .map(arg => arg.path)
            .filter(field => field.split('.').every(segment => !UNSAFE_KEY_SEGMENTS.has(segment)));
        if (!fields.length) return collection;

        const groups: { key: Record<string, TemplateContextValue>; items: TemplateContextValue[] }[] = [];
        const groupIndexByKey = new Map<string, number>();

        for (const item of collection as TemplateContextValue[]) {
            const keyValues: Record<string, TemplateContextValue> = {};
            for (const field of fields) this.assignNestedKey(keyValues, field, this.getNestedProperty(item, field));

            const compositeKey = fields.map(field => this.toCompositeKeyPart(this.getNestedProperty(item, field))).join('::');
            let groupIndex = groupIndexByKey.get(compositeKey);
            if (groupIndex === undefined) {
                groupIndex = groups.length;
                groupIndexByKey.set(compositeKey, groupIndex);
                groups.push({ key: keyValues, items: [] });
            }
            groups[groupIndex].items.push(item);
        }

        return groups;
    }

    private applySortBy(collection: TemplateContextValue, args: ExprNode[]): TemplateContextValue {
        if (!Array.isArray(collection)) return collection;

        const fields = args
            .filter((arg): arg is PathExprNode => arg.kind === 'path')
            .map(arg => arg.path)
            .filter(field => !SORT_DIRECTION_ARGS.has(field.toLowerCase()));
        if (!fields.length) return collection;

        const descending = args.some(arg => this.readSortDirection(arg) === DESCENDING_ARG);
        const direction = descending ? -1 : 1;
        const items = collection as TemplateContextValue[];

        return [...items].sort((left, right) => {
            for (const field of fields) {
                const order = this.compareSortValues(this.resolveSortField(left, field), this.resolveSortField(right, field));
                if (order !== 0) return order * direction;
            }
            return 0;
        });
    }

    private readSortDirection(arg: ExprNode): string | null {
        if (arg.kind === 'string') return arg.value.toLowerCase();
        if (arg.kind === 'path') return arg.path.toLowerCase();
        return null;
    }

    private toCompositeKeyPart(value: TemplateContextValue): string {
        const text = value === null || value === undefined ? '' : String(value);
        return `${text.length}:${text}`;
    }

    private assignNestedKey(target: Record<string, TemplateContextValue>, field: string, value: TemplateContextValue): void {
        const segments = field.split('.');
        if (segments.some(segment => UNSAFE_KEY_SEGMENTS.has(segment))) return;
        let current = target;

        for (const segment of segments.slice(0, -1)) {
            const existing = current[segment];
            const next = existing && typeof existing === 'object'
                ? existing as Record<string, TemplateContextValue>
                : {};
            current[segment] = next;
            current = next;
        }

        current[segments[segments.length - 1]] = value;
    }

    private resolveSortField(element: TemplateContextValue, field: string): TemplateContextValue {
        const groupKey = element && typeof element === 'object' ? (element as { key?: TemplateContextValue }).key : undefined;
        const fromKey = groupKey !== undefined ? this.getNestedProperty(groupKey, field) : undefined;
        return fromKey ?? this.getNestedProperty(element, field);
    }
}
